'use client'

import type { PropsWithChildren } from 'react'
import type { Control, Path } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import { NumericFormat, type NumericFormatProps, PatternFormat, type PatternFormatProps } from 'react-number-format'
import { Button, cn, Field, FieldError, FieldGroup, Input } from '@nwwp/ui-shadcn'

export const INPUT_MAX_WIDTH = 480

type FunnelStepProps = {
  children: React.ReactNode
  formRef?: React.RefObject<HTMLFormElement>
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void
}

export const FunnelStep = ({ children, onSubmit, formRef }: FunnelStepProps) => (
  <form onSubmit={onSubmit} ref={formRef} className="w-full">
    <FieldGroup className="gap-8 pb-24 text-white tablet:pb-0">{children}</FieldGroup>
  </form>
)

const Terminal = ({ children, ...props }: PropsWithChildren<React.ComponentProps<'div'>>) => (
  <div className="flex w-full flex-col gap-4 tablet:gap-8" {...props}>
    {children}
  </div>
)

const Title = ({ children }: PropsWithChildren) => (
  <h1 className="text-title-lg font-semibold text-white">{children}</h1>
)

const Subtitle = ({ children }: PropsWithChildren) => <p className="text-body-sm text-white">{children}</p>

const SubmitButton = ({ children, disabled }: PropsWithChildren<{ disabled?: boolean; loading?: boolean }>) => (
  <Button type="submit" variant="funnel" disabled={disabled} className="w-full max-w-120">
    {children}
  </Button>
)

type NumberInputProps<T extends Record<string, unknown>> = {
  control: Control<T>
  name: Path<T>
  prefix?: NumericFormatProps['prefix']
  placeholder?: NumericFormatProps['placeholder']
  decimalScale?: NumericFormatProps['decimalScale']
  allowNegative?: NumericFormatProps['allowNegative']
  thousandSeparator?: NumericFormatProps['thousandSeparator']
  allowLeadingZeros?: NumericFormatProps['allowLeadingZeros']
  pattern?: PatternFormatProps['format']
  disabled?: boolean
  maxChars?: number
  autoFocus?: boolean
  className?: string
}

const NumberInput = <T extends Record<string, unknown>>({
  control,
  name,
  prefix,
  placeholder,
  pattern,
  decimalScale = 0,
  allowNegative = false,
  thousandSeparator = false,
  allowLeadingZeros = false,
  disabled = false,
  maxChars,
  autoFocus = false,
  className,
}: NumberInputProps<T>) => (
  <Controller
    control={control}
    name={name}
    render={({ field, fieldState }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- react-number-format customInput typing does not preserve shadcn Input props.
      const CustomInput = Input as React.ComponentType<any>
      const sharedProps = {
        id: field.name,
        name: field.name,
        placeholder,
        type: 'tel' as const,
        inputMode: 'numeric' as const,
        value: (field.value as string | number | undefined) ?? '',
        disabled,
        autoFocus,
        'aria-invalid': fieldState.invalid || undefined,
        className: cn(
          'h-19 max-w-120 bg-neutral-surface [font-size:16px] text-white placeholder:text-neutral-darker tablet:[font-size:27px]',
          'shadow-[0_0_0_1px_var(--color-neutral-dark)]',
          'hover:shadow-[0_0_0_2px_var(--color-green-lighter)]',
          'focus:shadow-[0_0_0_2px_var(--color-green-lighter)]',
          className
        ),
        'aria-label': placeholder,
      }

      return (
        <Field data-invalid={fieldState.invalid}>
          {pattern ? (
            <PatternFormat
              format={pattern}
              customInput={CustomInput}
              onValueChange={({ value }) => field.onChange(value)}
              getInputRef={field.ref}
              {...sharedProps}
            />
          ) : (
            <NumericFormat
              prefix={prefix}
              allowNegative={allowNegative}
              thousandSeparator={thousandSeparator}
              decimalScale={decimalScale}
              allowLeadingZeros={allowLeadingZeros}
              customInput={CustomInput}
              onValueChange={({ value }) => field.onChange(value)}
              getInputRef={field.ref}
              isAllowed={(values) => (maxChars ? values.value.length <= maxChars : true)}
              {...sharedProps}
            />
          )}
          <FieldError errors={[fieldState.error]} />
        </Field>
      )
    }}
  />
)

const CurrencyInput = <T extends Record<string, unknown>>(props: NumberInputProps<T>) => (
  <NumberInput prefix="$ " thousandSeparator {...props} />
)

type TextInputProps<T extends Record<string, unknown>> = {
  control: Control<T>
  name: Path<T>
  placeholder: string
  autoFocus?: boolean
}

const TextInput = <T extends Record<string, unknown>>({ control, name, placeholder, autoFocus }: TextInputProps<T>) => (
  <Controller
    control={control}
    name={name}
    render={({ field, fieldState }) => (
      <Field data-invalid={fieldState.invalid}>
        <Input
          {...field}
          id={field.name}
          value={(field.value as string | number | readonly string[] | undefined) ?? ''}
          placeholder={placeholder}
          autoFocus={autoFocus}
          aria-invalid={fieldState.invalid || undefined}
          className="h-19 max-w-120 bg-neutral-surface [font-size:16px] text-white shadow-[0_0_0_1px_var(--color-neutral-dark)] placeholder:text-neutral-darker hover:shadow-[0_0_0_2px_var(--color-green-lighter)] focus:shadow-[0_0_0_2px_var(--color-green-lighter)] tablet:[font-size:27px]"
        />
        <FieldError errors={[fieldState.error]} />
      </Field>
    )}
  />
)

FunnelStep.Title = Title
FunnelStep.Button = SubmitButton
FunnelStep.NumberInput = NumberInput
FunnelStep.CurrencyInput = CurrencyInput
FunnelStep.TextInput = TextInput
FunnelStep.Terminal = Terminal
FunnelStep.Subtitle = Subtitle
